import { useEffect, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart,
} from "recharts";
import { toast } from "sonner";
import {
  Eye, Users, MousePointerClick, TrendingUp, Smartphone, Monitor, Tablet, Globe, Loader2,
} from "lucide-react";
import { API } from "@/lib/auth";

const RANGES = [
  { k: 7, l: "7d" },
  { k: 30, l: "30d" },
  { k: 90, l: "90d" },
];

// Prettify a raw testid/track key into a human label
function humanise(name) {
  if (!name) return "—";
  return name
    .replace(/^quiz-/, "Quiz · ")
    .replace(/^welcome-popup-?/, "Welcome popup · ")
    .replace(/^footer-/, "Footer · ")
    .replace(/^hero-/, "Hero · ")
    .replace(/^shop-/, "Shop · ")
    .replace(/-btn$/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function TrafficDashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/analytics/traffic`, { params: { days: d } });
      setData(r.data);
    } catch {
      toast.error("Could not load traffic analytics");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(days); }, [days]);

  if (loading && !data) {
    return (
      <div className="py-24 flex items-center justify-center text-[hsl(var(--onyx)/0.5)]">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }
  if (!data) return null;

  const totals = data.totals || {};
  const daily = data.daily || [];
  const topPages = data.top_pages || [];
  const topClicks = data.top_clicks || [];
  const topRefs = data.top_referrers || [];
  const devices = data.devices || [];
  const totalDeviceViews = devices.reduce((s, d) => s + d.count, 0) || 1;

  return (
    <div className="space-y-6" data-testid="admin-traffic-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="overline">Traffic & clicks</div>
          <p className="text-sm text-[hsl(var(--onyx)/0.6)] mt-1">
            First-party analytics from your live site — updates in real time, no cookie consent needed.
          </p>
        </div>
        <div className="inline-flex rounded-full bg-white border border-[hsl(var(--onyx)/0.1)] p-1">
          {RANGES.map((r) => (
            <button
              key={r.k}
              onClick={() => setDays(r.k)}
              data-testid={`traffic-range-${r.l}`}
              className={`px-3.5 py-1.5 text-xs uppercase tracking-widest rounded-full transition-colors ${
                days === r.k ? "bg-[hsl(var(--burgundy))] text-[hsl(var(--cream))]" : "text-[hsl(var(--onyx)/0.6)] hover:text-[hsl(var(--onyx))]"
              }`}
            >
              {r.l}
            </button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat Icon={Eye}         label="Pageviews today" value={totals.views_today} accent="burgundy" />
        <Stat Icon={TrendingUp}  label="Last 7 days"     value={totals.views_7d} />
        <Stat Icon={Eye}         label={`Total (${days}d)`} value={totals.views} />
        <Stat Icon={Users}       label="Unique visitors" value={totals.unique_sessions} />
        <Stat Icon={MousePointerClick} label={`Clicks (${days}d)`} value={totals.clicks} />
      </div>

      {/* Traffic chart */}
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Daily traffic</div>
            <p className="text-sm text-[hsl(var(--onyx)/0.55)] mt-1">Views vs. unique visitors, last {days} days</p>
          </div>
        </div>
        <div className="h-72 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(345 68% 35%)" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(345 68% 35%)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="sessGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(38 45% 60%)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(38 45% 60%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(26,26,26,0.06)" />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 11, fill: "rgba(26,26,26,0.55)" }}
                tickFormatter={(d) => d?.slice(5)}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 11, fill: "rgba(26,26,26,0.55)" }} allowDecimals={false} width={32} />
              <Tooltip
                contentStyle={{ borderRadius: 14, border: "1px solid rgba(26,26,26,0.08)", fontSize: 12 }}
                labelFormatter={(d) => new Date(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
              />
              <Area type="monotone" dataKey="views" name="Pageviews" stroke="hsl(345 68% 35%)" fill="url(#viewsGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="sessions" name="Unique visitors" stroke="hsl(38 45% 60%)" fill="url(#sessGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 3-up grid: top pages / top clicks / referrers + devices */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Most-visited pages" hint="Pageviews per URL">
          <RankList
            items={topPages.map((p) => ({ label: p.path === "/" ? "Home" : p.path, count: p.count }))}
            emptyLabel="No pageviews yet"
            testIdPrefix="top-page"
          />
        </Panel>

        <Panel title="Most-clicked buttons" hint="Buttons & links across your site">
          <RankList
            items={topClicks.map((c) => ({ label: humanise(c.name), sub: c.name, count: c.count }))}
            emptyLabel="No clicks recorded yet"
            testIdPrefix="top-click"
          />
        </Panel>

        <Panel title="Top referrers" hint="Where your traffic is coming from">
          <RankList
            items={topRefs.map((r) => ({ label: r.host, count: r.count, Icon: Globe }))}
            emptyLabel="Only direct traffic so far"
            testIdPrefix="top-ref"
          />
        </Panel>

        <Panel title="Device split" hint="Mobile · Tablet · Desktop">
          <div className="space-y-3">
            {devices.length === 0 && (
              <div className="text-sm text-[hsl(var(--onyx)/0.5)]">No data yet.</div>
            )}
            {devices.map((d) => {
              const pct = Math.round((d.count / totalDeviceViews) * 100);
              const Icon = d.device === "mobile" ? Smartphone : d.device === "tablet" ? Tablet : Monitor;
              return (
                <div key={d.device} data-testid={`device-${d.device}`} className="flex items-center gap-3">
                  <Icon size={16} className="shrink-0 text-[hsl(var(--burgundy))]" />
                  <div className="w-20 text-sm capitalize text-[hsl(var(--onyx)/0.8)]">{d.device}</div>
                  <div className="flex-1 h-2 rounded-full bg-[hsl(var(--onyx)/0.06)] overflow-hidden">
                    <div
                      className="h-full bg-[hsl(var(--burgundy))]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="w-16 text-right text-sm text-[hsl(var(--onyx)/0.6)]">
                    {d.count} <span className="text-[hsl(var(--onyx)/0.4)]">({pct}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>
    </div>
  );
}


// ---------- helpers ----------
function Stat({ Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5 ${accent === "burgundy" ? "ring-1 ring-[hsl(var(--burgundy)/0.15)]" : ""}`}>
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-[hsl(var(--burgundy))]" />
        <div className="overline">{label}</div>
      </div>
      <div className="font-display text-3xl mt-2">{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}

function Panel({ title, hint, children }) {
  return (
    <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
      <div className="overline">{title}</div>
      {hint && <p className="text-xs text-[hsl(var(--onyx)/0.5)] mt-1">{hint}</p>}
      <div className="mt-4">{children}</div>
    </div>
  );
}

function RankList({ items, emptyLabel, testIdPrefix }) {
  if (!items || items.length === 0) {
    return <div className="text-sm text-[hsl(var(--onyx)/0.5)]">{emptyLabel}</div>;
  }
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} data-testid={`${testIdPrefix}-${i}`} className="grid grid-cols-[24px,1fr,auto] items-center gap-3 py-1">
          <div className="text-xs text-[hsl(var(--onyx)/0.4)]">{i + 1}</div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {it.Icon && <it.Icon size={12} className="text-[hsl(var(--onyx)/0.4)]" />}
              <span className="text-sm text-[hsl(var(--onyx)/0.85)] truncate">{it.label}</span>
            </div>
            <div className="mt-1 h-1 rounded-full bg-[hsl(var(--onyx)/0.05)] overflow-hidden">
              <div className="h-full bg-[hsl(var(--burgundy))]" style={{ width: `${(it.count / max) * 100}%` }} />
            </div>
            {it.sub && it.sub !== it.label && (
              <div className="text-[10px] text-[hsl(var(--onyx)/0.4)] mt-1 truncate font-mono">{it.sub}</div>
            )}
          </div>
          <div className="text-sm text-[hsl(var(--onyx)/0.7)] tabular-nums">{it.count.toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}
